import type { Clock, IdGenerator } from "../ports/clock";
import type { DiaryRepository, WeekPlanRepository } from "../ports/repositories";
import type { TransactionManager } from "../ports/transaction-manager";
import {
  normalizeMealName,
  type DiaryEntry,
  type LoggedFoodSnapshot,
  type MealName,
} from "../../domain/diary/diary-entry";
import { assertLoggingAllowed, type LoggingIntent } from "../../domain/check-in/state-machine";
import { calendarDate, type CalendarDate } from "../../domain/shared/calendar-date";

export interface CopyMealInput {
  readonly sourceDate: CalendarDate;
  readonly meal: MealName;
  readonly targetDates: readonly CalendarDate[];
  readonly intent?: LoggingIntent;
}

/** Adds independent snapshots of one meal to each selected day in a single transaction. */
export async function copyMeal(
  dependencies: {
    readonly diary: DiaryRepository;
    readonly weekPlans: WeekPlanRepository;
    readonly transaction: TransactionManager;
    readonly clock: Clock;
    readonly ids: IdGenerator;
  },
  input: CopyMealInput,
): Promise<readonly DiaryEntry[]> {
  const sourceDate = calendarDate(input.sourceDate);
  const targetDates = [...new Set(input.targetDates.map(calendarDate))];
  if (targetDates.length === 0) throw new Error("Choose at least one day to copy this meal to");
  if (targetDates.includes(sourceDate)) throw new Error("The source day cannot also be a copy destination");

  return dependencies.transaction.run(async () => {
    const plan = await dependencies.weekPlans.getOpen();
    if (!plan) throw new Error("No active week plan");
    assertLoggingAllowed(plan, dependencies.clock.today(), sourceDate, input.intent);
    targetDates.forEach((date) => assertLoggingAllowed(plan, dependencies.clock.today(), date, input.intent));

    const sourceEntries = (await dependencies.diary.listBetween(sourceDate, sourceDate))
      .filter((entry) => normalizeMealName(entry.meal) === input.meal);
    if (sourceEntries.length === 0) throw new Error(`There is nothing in ${input.meal.toLowerCase()} to copy`);

    const now = dependencies.clock.now();
    const copies = targetDates.flatMap((date) => sourceEntries.map((entry) => ({
      id: dependencies.ids.next(),
      date,
      meal: input.meal,
      food: cloneFoodSnapshot(entry.food),
      createdAt: now,
      updatedAt: now,
    } satisfies DiaryEntry)));

    for (const entry of copies) await dependencies.diary.save(entry);
    return copies;
  });
}

function cloneFoodSnapshot(food: LoggedFoodSnapshot): LoggedFoodSnapshot {
  return {
    ...food,
    basis: { ...food.basis },
    ...(food.measures === undefined ? {} : { measures: food.measures.map((measure) => ({ ...measure })) }),
    portion: {
      ...food.portion,
      ...(food.portion.display === undefined ? {} : { display: { ...food.portion.display } }),
    },
    nutrients: {
      ...food.nutrients,
      macros: { ...food.nutrients.macros },
      micronutrients: Object.fromEntries(
        Object.entries(food.nutrients.micronutrients).map(([key, nutrient]) => [key, { ...nutrient }]),
      ),
    },
  };
}
